import React, { useEffect, useRef, useState } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { fromLonLat } from 'ol/proj';
import { Icon, Style, Stroke, Fill, Text, Circle as CircleStyle } from 'ol/style';
import Overlay from 'ol/Overlay';
import { defaults as defaultControls, FullScreen, ScaleLine, ZoomToExtent } from 'ol/control';
import 'ol/ol.css';
import { Box, IconButton, Tooltip, Paper } from '@mui/material';
import {
  MyLocation as MyLocationIcon,
  Layers as LayersIcon,
  FilterCenterFocus as FilterCenterFocusIcon
} from '@mui/icons-material';

/**
 * Mapa mejorado con OpenLayers
 * Incluye iconos personalizados, controles avanzados y soporte para rutas
 */
const OpenLayersMapEnhanced = ({
  workers,
  selectedWorker,
  onWorkerClick,
  routeData,
  historyPoints
}) => {
  const mapRef = useRef();
  const mapInstanceRef = useRef();
  const vectorLayerRef = useRef();
  const routeLayerRef = useRef();
  const historyLayerRef = useRef();
  const popupOverlayRef = useRef();
  const [mapReady, setMapReady] = useState(false);

  // Inicializar mapa
  useEffect(() => {
    if (!mapInstanceRef.current) {
      // Fuente de datos para marcadores de trabajadores
      const vectorSource = new VectorSource();
      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: createWorkerMarkerStyle,
        zIndex: 10
      });

      // Capa para ruta (polyline)
      const routeSource = new VectorSource();
      const routeLayer = new VectorLayer({
        source: routeSource,
        style: new Style({
          stroke: new Stroke({
            color: '#2196F3',
            width: 4,
            lineDash: undefined
          })
        }),
        zIndex: 5
      });

      // Capa para puntos del historial
      const historySource = new VectorSource();
      const historyLayer = new VectorLayer({
        source: historySource,
        style: createHistoryPointStyle,
        zIndex: 7
      });

      // Crear mapa
      const map = new Map({
        target: mapRef.current,
        layers: [
          new TileLayer({
            source: new OSM()
          }),
          routeLayer,
          historyLayer,
          vectorLayer
        ],
        view: new View({
          center: fromLonLat([-77.0428, -12.0464]), // Lima, Perú por defecto
          zoom: 12,
          maxZoom: 20,
          minZoom: 3
        }),
        controls: defaultControls().extend([
          new FullScreen(),
          new ScaleLine({
            units: 'metric'
          })
        ])
      });

      // Crear overlay para popup
      const popup = document.createElement('div');
      popup.className = 'ol-popup';

      const popupOverlay = new Overlay({
        element: popup,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -40]
      });

      map.addOverlay(popupOverlay);

      // Click en marcadores
      map.on('click', (evt) => {
        const feature = map.forEachFeatureAtPixel(evt.pixel, (feature) => feature);

        if (feature && feature.get('worker')) {
          const worker = feature.get('worker');
          showPopup(worker, evt.coordinate, popupOverlay, popup);
          if (onWorkerClick) {
            onWorkerClick(worker);
          }
        } else {
          popupOverlay.setPosition(undefined);
        }
      });

      // Cambiar cursor al pasar sobre marcadores
      map.on('pointermove', (evt) => {
        const hit = map.hasFeatureAtPixel(evt.pixel);
        map.getTargetElement().style.cursor = hit ? 'pointer' : '';
      });

      mapInstanceRef.current = map;
      vectorLayerRef.current = vectorLayer;
      routeLayerRef.current = routeLayer;
      historyLayerRef.current = historyLayer;
      popupOverlayRef.current = popupOverlay;
      setMapReady(true);
    }
  }, []);

  // Actualizar marcadores de trabajadores
  useEffect(() => {
    if (!vectorLayerRef.current || !workers || workers.length === 0) return;

    const source = vectorLayerRef.current.getSource();
    source.clear();

    workers.forEach(worker => {
      if (worker.latitud && worker.longitud) {
        const feature = new Feature({
          geometry: new Point(fromLonLat([parseFloat(worker.longitud), parseFloat(worker.latitud)])),
          worker: worker
        });

        source.addFeature(feature);
      }
    });

    // Ajustar vista para mostrar todos los trabajadores
    if (workers.length > 0 && !selectedWorker) {
      const extent = source.getExtent();
      if (extent && extent.every(val => isFinite(val))) {
        mapInstanceRef.current.getView().fit(extent, {
          padding: [80, 80, 80, 80],
          maxZoom: 16,
          duration: 1000
        });
      }
    }
  }, [workers]);

  // Zoom a trabajador seleccionado
  useEffect(() => {
    if (!mapInstanceRef.current || !selectedWorker) return;

    const view = mapInstanceRef.current.getView();
    if (selectedWorker.latitud && selectedWorker.longitud) {
      view.animate({
        center: fromLonLat([parseFloat(selectedWorker.longitud), parseFloat(selectedWorker.latitud)]),
        zoom: 17,
        duration: 1000
      });
    }
  }, [selectedWorker]);

  // Mostrar ruta
  useEffect(() => {
    if (!routeLayerRef.current) return;

    const routeSource = routeLayerRef.current.getSource();
    routeSource.clear();

    if (routeData && routeData.ruta && routeData.ruta.length > 0) {
      const coordinates = routeData.ruta.map(coord => fromLonLat(coord));

      const routeFeature = new Feature({
        geometry: new LineString(coordinates)
      });

      routeSource.addFeature(routeFeature);

      // Ajustar vista a la ruta
      const extent = routeSource.getExtent();
      mapInstanceRef.current.getView().fit(extent, {
        padding: [80, 80, 80, 80],
        duration: 1000
      });
    }
  }, [routeData]);

  // Mostrar puntos del historial
  useEffect(() => {
    if (!historyLayerRef.current) return;

    const historySource = historyLayerRef.current.getSource();
    historySource.clear();

    if (historyPoints && historyPoints.length > 0) {
      historyPoints.forEach((point, index) => {
        const feature = new Feature({
          geometry: new Point(fromLonLat([parseFloat(point.longitud), parseFloat(point.latitud)])),
          historyPoint: point,
          index: index
        });

        historySource.addFeature(feature);
      });

      // Ajustar vista a los puntos
      const extent = historySource.getExtent();
      mapInstanceRef.current.getView().fit(extent, {
        padding: [80, 80, 80, 80],
        duration: 1000
      });
    }
  }, [historyPoints]);

  /**
   * Centrar mapa en ubicación actual
   */
  const handleCenterMap = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const view = mapInstanceRef.current.getView();
        view.animate({
          center: fromLonLat([position.coords.longitude, position.coords.latitude]),
          zoom: 15,
          duration: 1000
        });
      });
    }
  };

  /**
   * Ajustar vista a todos los trabajadores
   */
  const handleFitAll = () => {
    if (!vectorLayerRef.current) return;

    const source = vectorLayerRef.current.getSource();
    const extent = source.getExtent();

    if (extent && extent.every(val => isFinite(val))) {
      mapInstanceRef.current.getView().fit(extent, {
        padding: [80, 80, 80, 80],
        maxZoom: 16,
        duration: 1000
      });
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      />

      {/* Controles personalizados */}
      {mapReady && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 1000,
            borderRadius: 1
          }}
        >
          <Tooltip title="Centrar en mi ubicación">
            <IconButton onClick={handleCenterMap} size="small">
              <MyLocationIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver todos los trabajadores">
            <IconButton onClick={handleFitAll} size="small">
              <FilterCenterFocusIcon />
            </IconButton>
          </Tooltip>
        </Paper>
      )}

      <style jsx>{`
        .ol-popup {
          background: white;
          border-radius: 8px;
          padding: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          min-width: 220px;
        }
        .ol-popup::before {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 10px solid transparent;
          border-right: 10px solid transparent;
          border-top: 10px solid white;
        }
        .worker-popup-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e0e0e0;
        }
        .worker-popup-body p {
          margin: 4px 0;
          font-size: 13px;
        }
        .status-badge {
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .status-online {
          background: #4caf50;
          color: white;
        }
        .status-idle {
          background: #ffc107;
          color: #000;
        }
        .status-offline {
          background: #9e9e9e;
          color: white;
        }
      `}</style>
    </Box>
  );
};

/**
 * Crear icono personalizado para trabajador
 */
function createWorkerMarkerStyle(feature) {
  const worker = feature.get('worker');
  if (!worker) return null;

  const color = getWorkerColor(worker.estado);
  const iconSvg = createWorkerIconSVG(color, worker.estado);

  return new Style({
    image: new Icon({
      src: `data:image/svg+xml;utf8,${encodeURIComponent(iconSvg)}`,
      scale: 1,
      anchor: [0.5, 1]
    }),
    text: new Text({
      text: worker.nombre_completo || worker.nombre || '',
      offsetY: -45,
      fill: new Fill({ color: '#000' }),
      stroke: new Stroke({ color: '#fff', width: 3 }),
      font: 'bold 12px sans-serif'
    })
  });
}

/**
 * Crear estilo para puntos del historial
 */
function createHistoryPointStyle(feature) {
  const index = feature.get('index');
  const isFirst = index === 0;
  const isLast = feature.get('historyPoint')?.isLast;

  return new Style({
    image: new CircleStyle({
      radius: isFirst || isLast ? 8 : 5,
      fill: new Fill({ color: isFirst ? '#4CAF50' : isLast ? '#F44336' : '#2196F3' }),
      stroke: new Stroke({ color: '#fff', width: 2 })
    })
  });
}

/**
 * Generar SVG de icono de trabajador
 */
function createWorkerIconSVG(color, estado) {
  const pulseAnimation = estado === 'online' ? `
    <circle cx="20" cy="15" r="15" fill="${color}" opacity="0.3">
      <animate attributeName="r" from="15" to="25" dur="1.5s" repeatCount="indefinite" />
      <animate attributeName="opacity" from="0.3" to="0" dur="1.5s" repeatCount="indefinite" />
    </circle>
  ` : '';

  return `
    <svg width="40" height="50" xmlns="http://www.w3.org/2000/svg">
      ${pulseAnimation}
      <!-- Pin principal -->
      <path d="M20,5 C13.9,5 9,9.9 9,16 C9,24 20,35 20,35 S31,24 31,16 C31,9.9 26.1,5 20,5 Z"
            fill="${color}" stroke="#fff" stroke-width="2"/>
      <!-- Icono de persona -->
      <circle cx="20" cy="14" r="3" fill="#fff"/>
      <path d="M15,22 Q20,20 25,22 L24,27 L16,27 Z" fill="#fff"/>
      <!-- Indicador de estado -->
      <circle cx="28" cy="10" r="4" fill="${getStatusIndicatorColor(estado)}" stroke="#fff" stroke-width="1.5"/>
    </svg>
  `;
}

/**
 * Obtener color según estado
 */
function getWorkerColor(estado) {
  switch (estado) {
    case 'online':
      return '#4CAF50';
    case 'idle':
      return '#FFC107';
    default:
      return '#9E9E9E';
  }
}

/**
 * Obtener color del indicador de estado
 */
function getStatusIndicatorColor(estado) {
  switch (estado) {
    case 'online':
      return '#00E676';
    case 'idle':
      return '#FFD54F';
    default:
      return '#BDBDBD';
  }
}

/**
 * Mostrar popup con información del trabajador
 */
function showPopup(worker, coordinate, overlay, element) {
  const estado = worker.estado === 'online' ? 'En línea' :
                worker.estado === 'idle' ? 'Inactivo' : 'Desconectado';

  const velocidad = worker.velocidad ? `${(worker.velocidad * 3.6).toFixed(1)} km/h` : 'Detenido';

  const ultimaActualizacion = worker.segundos_desde_actualizacion
    ? formatearTiempo(worker.segundos_desde_actualizacion)
    : 'Ahora';

  element.innerHTML = `
    <div class="worker-popup-header">
      <strong>${worker.nombre_completo || `${worker.nombre} ${worker.apellido}`}</strong>
      <span class="status-badge status-${worker.estado}">${estado}</span>
    </div>
    <div class="worker-popup-body">
      <p><strong>🚗 Velocidad:</strong> ${velocidad}</p>
      <p><strong>🕒 Actualizado:</strong> ${ultimaActualizacion}</p>
      <p><strong>📍 Coords:</strong> ${parseFloat(worker.latitud).toFixed(5)}, ${parseFloat(worker.longitud).toFixed(5)}</p>
      ${worker.precision ? `<p><strong>📏 Precisión:</strong> ±${parseFloat(worker.precision).toFixed(1)}m</p>` : ''}
    </div>
  `;

  overlay.setPosition(coordinate);
}

/**
 * Formatear segundos a texto legible
 */
function formatearTiempo(segundos) {
  if (segundos < 60) return 'Hace unos segundos';
  if (segundos < 3600) return `Hace ${Math.floor(segundos / 60)} min`;
  const hours = Math.floor(segundos / 3600);
  return `Hace ${hours} hora${hours > 1 ? 's' : ''}`;
}

export default OpenLayersMapEnhanced;
